import {
  Component,
  computed,
  DestroyRef,
  HostListener,
  OnInit,
  signal,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { Store } from '@ngrx/store';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import { combineLatest, Observable, of } from 'rxjs';
import { MarketType } from '../bids-and-offers/lots.model';
import { UserState } from 'src/app/store/user/user.reducer';
import { environment } from 'src/environments/environment';
import { WordPressService } from 'src/app/services/word-press.service';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BlockUserRolesDirective } from 'src/app/directives/block-user-roles.directive';
import { ThemeService } from 'src/app/services/theme.service';
import { faMoon, faSun } from '@fortawesome/free-regular-svg-icons';
import { MatBadgeModule } from '@angular/material/badge';
import { MatRipple } from '@angular/material/core';
import { selectUser } from 'src/app/store/user/user.selectors';
import { selectTableData } from 'src/app/store/table-data/table-data.selectors';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  changeTableMarket,
  fetchData,
} from 'src/app/store/table-data/table-data.actions';
import { fetchNegotiations } from 'src/app/store/negotiations/negotiations.actions';
import {
  selectActiveNegotiations,
  selectNegotiations,
} from 'src/app/store/negotiations/negotiations.selectors';

@Component({
  selector: 'app-nav-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    NgOptimizedImage,
    RouterModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatTooltipModule,
    MatChipsModule,
    FormsModule,
    ReactiveFormsModule,
    MatRipple,
    MatFormFieldModule,
    MatInputModule,
    FontAwesomeModule,
    MatBadgeModule,
    MatSelectModule,
    BlockUserRolesDirective,
    MatTooltipModule,
  ],
  templateUrl: './nav-toolbar.component.html',
  styleUrls: ['./nav-toolbar.component.scss'],
  animations: [
    trigger('dropdownAnimation', [
      state(
        'void',
        style({
          transform: 'scale(0.95)',
          opacity: 0,
        })
      ),
      transition('void => *', [
        animate(
          '100ms ease-out',
          style({
            transform: 'scale(1)',
            opacity: 1,
          })
        ),
      ]),
      transition('* => void', [
        animate(
          '75ms ease-in',
          style({
            transform: 'scale(0.95)',
            opacity: 0,
          })
        ),
      ]),
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('800ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class NavToolbarComponent implements OnInit {
  faMoon = faMoon;
  faSun = faSun;
  isLightMode = computed(() => this.themeService.isLightMode());
  isProd = environment.production;
  links = environment.links;
  isMenuOpened = false;
  isProfileOpened = false;
  feature: string;
  market$: Observable<MarketType>;
  negotiations = signal(0);
  userMarkets = signal<MarketType[]>([]);
  showActiveMarket = signal<boolean>(false);
  userInitials = signal('');

  newDaily = signal<boolean>(false);
  constructor(
    private router: Router,
    private authService: AuthService,
    private store: Store<{ lotTableData: TableDataState; user: UserState }>,
    readonly wordPressService: WordPressService,
    readonly themeService: ThemeService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects;
        this.setFeatureFromUrl(url);
      });

    this.market$ = this.store
      .select(selectTableData)
      .pipe(map((res) => res.selectedMarket));

    this.store
      .select(selectActiveNegotiations)
      .pipe(
        map((negs) => {
          if (!negs) {
            this.store.dispatch(fetchNegotiations());
            return;
          }
          this.negotiations.set(negs.length);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        map((user) => {
          if (user) {
            this.userInitials.set(user.first_name[0] + user.last_name[0]);
            this.showActiveMarket.set(user.market_options.length > 1);
            this.userMarkets.set(user.market_options);
          }
        })
      )
      .subscribe();
  }

  changeMarket(market: MarketType) {
    this.store.dispatch(
      fetchData({ limit: 40, marketType: MarketType[market] })
    );
    this.store.dispatch(changeTableMarket({ marketType: MarketType[market] }));
  }

  setFeatureFromUrl(url: string) {
    // Logic to determine feature based on URL
    if (url.includes('app/lots')) {
      this.feature = 'lots';
    } else if (url.includes('app/trades')) {
      this.feature = 'trades';
    } else if (url.includes('app/negotiations')) {
      this.feature = 'negotiations';
    } else if (url.includes('app/chat')) {
      this.feature = 'chat';
    } else if (url.includes('app/contracts')) {
      this.feature = 'contracts';
    } else if (url.includes('app/faqs')) {
      this.feature = 'faqs';
    }
  }

  onSelect(feature: string) {
    this.feature = feature;
    this.isMenuOpened = false;
  }

  toggleMenu() {
    this.isMenuOpened = !this.isMenuOpened;
  }

  toggleProfile() {
    this.isProfileOpened = !this.isProfileOpened;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const targetElement = event.target as HTMLElement;

    // Check if the clicked element is outside the profile dropdown
    if (!this.isDescendant(targetElement, 'profile-dropdown')) {
      this.isProfileOpened = false;
    }

    // Check if the clicked element is outside the profile dropdown
    if (
      !this.isDescendant(targetElement, 'mobile-menu') &&
      !this.isDescendant(targetElement, 'mobile-menu-button')
    ) {
      this.isMenuOpened = false;
    }
  }

  private isDescendant(element: HTMLElement, parentId: string): boolean {
    let node = element;

    while (node !== null && node.nodeType === 1) {
      if (node.id === parentId) {
        return true;
      }
      node = node.parentNode as HTMLElement;
    }

    return false;
  }

  onLogout() {
    this.authService.logout();
  }
}
